import { LegalPageShell } from "@/components/app/LegalPageShell";
import { usePageTitle } from "../hooks/usePageTitle";

export function PrivacyPolicyPage() {
  usePageTitle("Privacy Policy | TidyRota");

  return (
    <LegalPageShell
      title="Privacy Policy"
      lastUpdated="22 April 2026"
      summary={
        <>
          We collect the information we need to run TidyRota and nothing more.
          We don&rsquo;t sell your data. We use a small number of trusted
          providers (Stripe for payments, Resend for email, Postcodes.io for
          postcode lookups, DigitalOcean for hosting and backups, Google Ads
          for measuring which adverts work). You can ask us to see, correct,
          or delete your data at any time.
        </>
      }
    >
      <section>
        <h2>1. Who we are</h2>
        <p>
          TidyRota is a trading name of Jack Hodkinson, a sole trader
          registered in England &amp; Wales.
        </p>
        <p>
          Contact:{" "}
          <a href="mailto:hello@tidyrota.com">hello@tidyrota.com</a>
          <br />
          Post: TidyRota, 152 Leeds Road, Wakefield, WF1 2QA, United Kingdom.
        </p>
        <p>
          Under UK data protection law (the UK GDPR and the Data Protection
          Act 2018), we are the <strong>controller</strong> of the personal
          data you give us when you sign up, pay us, or contact us. When
          your cleaning company uploads data about its staff, clients, and
          sites to run its business, your cleaning company is the
          controller of that data and we are the{" "}
          <strong>processor</strong>, acting on its behalf.
        </p>
      </section>

      <section>
        <h2>2. What we collect and why</h2>
        <p>
          Data protection law asks us to have a clear reason for each use
          of your personal data. For most of what&rsquo;s below we rely on
          our contract with you (we can&rsquo;t run the service without
          the data), or on legitimate interests in running, billing for,
          and protecting the service. Where the basis sits with your
          employer rather than us, we call it out.
        </p>

        <h3>a) When you sign up for an account</h3>
        <ul>
          <li>
            Your name and email address, so we can log you in and contact you
            about the service.
          </li>
          <li>
            A password, stored as a one-way hash (we can&rsquo;t see your
            actual password).
          </li>
          <li>Your phone number, if you choose to add one.</li>
          <li>Your cleaning company&rsquo;s name.</li>
        </ul>

        <h3>b) When you use TidyRota to run your cleaning company</h3>
        <p>
          If you&rsquo;re an owner or manager, you&rsquo;ll add details about:
        </p>
        <ul>
          <li>
            Cleaning sites (name, address, postcode, client contact email, a
            location pin, a check-in radius).
          </li>
          <li>
            Your team (names, email addresses, optional phone numbers,
            roles).
          </li>
          <li>Shifts (which cleaner, which site, what time).</li>
          <li>Invites to new team members.</li>
        </ul>
        <p>
          You are the controller of this data. We only use it to run the
          service for you, and we act on your instructions.
        </p>

        <h3>c) When cleaners check in and out of shifts</h3>
        <p>
          If you&rsquo;re a cleaner, when you press &ldquo;I&rsquo;m
          here&rdquo; or &ldquo;I&rsquo;m done&rdquo; in the app, we record:
        </p>
        <ul>
          <li>A timestamp.</li>
          <li>
            Your location, if you grant the app permission to read it:
            latitude, longitude, and your phone&rsquo;s estimate of how
            accurate that is. If you don&rsquo;t grant permission the
            check-in still works, and we record that the location
            wasn&rsquo;t available.
          </li>
          <li>How far you were from the cleaning site at check-in.</li>
        </ul>
        <p>
          Your employer is the controller of this data, not us. They use
          it to verify attendance and share reports with their clients,
          relying on their legitimate interest in managing their cleaning
          business. Your employer is responsible for telling you, clearly
          and in advance, that location is captured at check-in.
        </p>

        <h3>d) When you pay us</h3>
        <p>
          Payments go through Stripe. We never see your card details. We
          store your Stripe customer ID, your subscription status, and your
          invoice history so we can bill you and show your history inside
          the app.
        </p>

        <h3>e) When you visit our website</h3>
        <ul>
          <li>
            For sensitive requests (log in, sign up, reset password, team
            invites), we count requests per IP address to block
            brute-force attacks. These counts live in memory only and are
            never written to disk.
          </li>
          <li>
            We count page views so we can see which parts of the site are
            being used and whether our marketing is working. For each page
            view we store the path you visited (for example{" "}
            <code>/signup</code>), the hostname that sent you to us (for
            example <code>google.co.uk</code>), any{" "}
            <code>utm_*</code> marketing tags in the URL, and the{" "}
            <code>gclid</code> click ID if you came from a Google advert.
            To count unique visitors without tracking you, we take your
            IP address and browser user agent, combine them with a secret
            that rotates every day, and keep only the resulting one-way
            hash. We don&rsquo;t store your IP address, we don&rsquo;t
            set any cookies for this, and yesterday&rsquo;s hash cannot
            be linked to today&rsquo;s. We rely on our legitimate interest
            in understanding how the site is used.
          </li>
          <li>
            If you visit us after clicking a Google advert, we read the{" "}
            <code>gclid</code> parameter from the URL and keep it in your
            browser&rsquo;s session storage for the duration of your visit.
            If you then sign up or finish setting up your account, our
            server sends Google that click ID together with a one-way
            hashed (SHA-256) form of your email address so Google can
            measure which adverts converted and, if you&rsquo;re signed
            into a Google account, match the sign-up to it. We never
            share your raw email, your name, or any other personal
            details with Google. No Google script runs in your browser.
          </li>
        </ul>

        <h3>f) When you email us</h3>
        <p>
          We keep a log of transactional emails we send you (the template,
          subject, recipient, and whether sending succeeded) so we can help
          if a message goes missing. The content of your replies lives in
          our normal email inbox.
        </p>
      </section>

      <section>
        <h2>3. Cookies and similar technologies</h2>
        <p>TidyRota uses the smallest possible number of cookies.</p>

        <h3>Essential cookies (no consent needed)</h3>
        <ul>
          <li>
            <strong>
              <code>session_id</code>
            </strong>{" "}
            keeps you signed in. It&rsquo;s HTTP-only, uses the
            &ldquo;Lax&rdquo; same-site policy, and lasts 30 days from
            your last visit. Without it, you can&rsquo;t stay signed in.
          </li>
        </ul>

        <h3>Advertising measurement (no cookie)</h3>
        <p>
          If you arrive from a Google advert, we read the{" "}
          <code>gclid</code> URL parameter and keep it in your
          browser&rsquo;s session storage (cleared when you close the tab)
          until you finish signing up. When you sign up, and again when you
          finish setting up your account, our server calls Google directly
          with the click ID and a SHA-256 hash of your email. We don&rsquo;t
          load any Google tracking script in your browser, and your raw
          email never leaves our server.
        </p>

        <h3>Page-view counting (no cookie)</h3>
        <p>
          We count page views on our own servers without setting any
          cookies or storing anything on your device. Unique visitors are
          identified by a daily-rotating one-way hash of your IP address
          and browser user agent, as described in section 2(e). No
          third-party analytics or tracking tools run in your browser.
        </p>

        <p>
          We don&rsquo;t use advertising cookies or third-party tracking
          tools.
        </p>
      </section>

      <section>
        <h2>4. Who we share data with</h2>
        <p>
          TidyRota is a small service and uses a small number of carefully
          chosen providers to run it. Each provider is bound by a contract
          that limits what they can do with your data.
        </p>
        <table>
          <thead>
            <tr>
              <th>Provider</th>
              <th>What they do</th>
              <th>What we share</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <strong>Stripe Payments Europe Ltd</strong> (Ireland)
              </td>
              <td>Takes payments and stores card details.</td>
              <td>
                Your name, email, the cleaning company&rsquo;s name, how
                many cleaners you have, invoice amounts. We never see or
                store card details.
              </td>
            </tr>
            <tr>
              <td>
                <strong>Resend, Inc.</strong> (USA)
              </td>
              <td>
                Sends our transactional email (invites, password resets,
                billing notices).
              </td>
              <td>The recipient&rsquo;s email and the message contents.</td>
            </tr>
            <tr>
              <td>
                <strong>Postcodes.io</strong> (UK)
              </td>
              <td>
                Turns a UK postcode into a latitude and longitude so we can
                show a cleaning site on a map.
              </td>
              <td>The postcode you enter. No personal details.</td>
            </tr>
            <tr>
              <td>
                <strong>DigitalOcean, LLC</strong> (London data centre)
              </td>
              <td>
                Hosts the TidyRota app and stores our encrypted database
                backups.
              </td>
              <td>
                All data you put into TidyRota, at rest and in transit,
                within the London region.
              </td>
            </tr>
            <tr>
              <td>
                <strong>Google Ireland Ltd</strong> (Google Ads)
              </td>
              <td>
                Measures which of our adverts led to sign-ups.
              </td>
              <td>
                The <code>gclid</code> click ID, the fact that a sign-up
                happened, a timestamp, an internal order ID, and a
                one-way SHA-256 hash of your email. Never your raw email
                or name.
              </td>
            </tr>
          </tbody>
        </table>
        <p>
          We do not sell your personal data, and we will never do so.
        </p>
      </section>

      <section>
        <h2>5. Where your data lives</h2>
        <p>
          Your data is stored in the United Kingdom (DigitalOcean&rsquo;s
          London data centre). Some of our providers (Stripe, Resend,
          Google) may process data outside the UK. When data is transferred
          outside the UK, we rely on the UK International Data Transfer
          Agreement or the EU Standard Contractual Clauses with the UK
          Addendum, so your data keeps broadly the same level of
          protection.
        </p>
      </section>

      <section>
        <h2>6. How long we keep it</h2>
        <p>We keep your data while your account is active.</p>
        <ul>
          <li>
            <strong>Sessions</strong> are deleted 30 days after your last
            visit.
          </li>
          <li>
            <strong>Password reset tokens</strong> expire after 1 hour.
          </li>
          <li>
            <strong>Database backups</strong> are taken daily and stored in
            DigitalOcean Spaces in London.
          </li>
          <li>
            <strong>Account and operational data</strong> is kept while
            you&rsquo;re a customer and for a reasonable period after you
            cancel, so we can answer questions and meet legal obligations.
          </li>
          <li>
            <strong>Invoice and billing records</strong> are kept for 6
            years, as required by HMRC.
          </li>
        </ul>
      </section>

      <section>
        <h2>7. Your rights</h2>
        <p>
          Under UK data protection law, you have the right to:
        </p>
        <ul>
          <li>
            <strong>See</strong> the personal data we hold about you.
          </li>
          <li>
            <strong>Correct</strong> anything that&rsquo;s wrong.
          </li>
          <li>
            <strong>Delete</strong> your data (we may keep records we need
            for legal or accounting reasons).
          </li>
          <li>
            <strong>Restrict</strong> how we use it.
          </li>
          <li>
            <strong>Object</strong> to us using it.
          </li>
          <li>
            <strong>Take it with you</strong> (portability) in a common
            format.
          </li>
          <li>
            <strong>Withdraw consent</strong> at any time, where we rely on
            consent.
          </li>
          <li>
            <strong>Complain</strong> to the Information Commissioner&rsquo;s
            Office (ico.org.uk) if you think we&rsquo;ve got something
            wrong. We&rsquo;d rather you told us first so we can fix it.
            Email{" "}
            <a href="mailto:hello@tidyrota.com">hello@tidyrota.com</a>.
          </li>
        </ul>
        <p>
          Some of the data in TidyRota is held on your employer&rsquo;s
          behalf (for example, check-in records). For those, you may need
          to ask your employer first. We&rsquo;ll help them answer any
          request you make.
        </p>
      </section>

      <section>
        <h2>8. Staff access</h2>
        <p>
          TidyRota has a very small team. A small number of named staff can
          view organisation data through an internal admin tool, to help
          when things go wrong, to investigate abuse, and to support paying
          customers. Every staff member is bound by confidentiality. We log
          administrative actions that change state (suspensions, trial
          extensions, billing fixes).
        </p>
      </section>

      <section>
        <h2>9. Security</h2>
        <ul>
          <li>
            Passwords are hashed with Argon2id. We never store or send
            passwords in plain text.
          </li>
          <li>All traffic to TidyRota is over HTTPS.</li>
          <li>
            Payments run through Stripe, who are PCI DSS Level 1 certified.
          </li>
          <li>We take daily, encrypted backups.</li>
        </ul>
        <p>
          No service is perfectly secure, but we take the basics seriously.
        </p>
      </section>

      <section>
        <h2>10. Children</h2>
        <p>
          TidyRota is a business tool for commercial cleaning companies.
          It&rsquo;s not intended for children under 16, and we don&rsquo;t
          knowingly collect personal data from children.
        </p>
      </section>

      <section>
        <h2>11. Changes to this policy</h2>
        <p>
          When we change this policy, we&rsquo;ll update the &ldquo;Last
          updated&rdquo; date at the top. If the change is significant,
          we&rsquo;ll also email account holders.
        </p>
      </section>

      <section>
        <h2>12. Contact us</h2>
        <p>
          If you have a question about this policy or how we use your data,
          email{" "}
          <a href="mailto:hello@tidyrota.com">hello@tidyrota.com</a> or
          write to:
        </p>
        <p>
          TidyRota
          <br />
          152 Leeds Road
          <br />
          Wakefield
          <br />
          WF1 2QA
          <br />
          United Kingdom
        </p>
      </section>
    </LegalPageShell>
  );
}
